/**
 * Helper para exibir fusos horários em horários de audiências no frontend
 */

export interface TimezoneInfo {
  timezone: string;
  abbreviation: string;
  displayName: string;
  utcOffset: string;
}

/**
 * Timezone constants - shared objects to avoid duplication
 */
const TIMEZONE_ACT: TimezoneInfo = {
  timezone: 'America/Rio_Branco',
  abbreviation: 'ACT',
  displayName: 'horário do Acre',
  utcOffset: '-05:00',
};

const TIMEZONE_AMT: TimezoneInfo = {
  timezone: 'America/Manaus',
  abbreviation: 'AMT',
  displayName: 'horário do Amazonas',
  utcOffset: '-04:00',
};

const TIMEZONE_BRT: TimezoneInfo = {
  timezone: 'America/Sao_Paulo',
  abbreviation: 'BRT',
  displayName: 'horário de Brasília',
  utcOffset: '-03:00',
};

/**
 * State-specific timezone overrides (for states with different IANA IDs)
 */
const STATE_SPECIFIC_TIMEZONES: Record<string, TimezoneInfo> = {
  RR: {
    timezone: 'America/Boa_Vista',
    abbreviation: 'AMT',
    displayName: 'horário de Roraima',
    utcOffset: '-04:00',
  },
  RO: {
    timezone: 'America/Porto_Velho',
    abbreviation: 'AMT',
    displayName: 'horário de Rondônia',
    utcOffset: '-04:00',
  },
  MT: {
    timezone: 'America/Cuiaba',
    abbreviation: 'AMT',
    displayName: 'horário de Mato Grosso',
    utcOffset: '-04:00',
  },
  MS: {
    timezone: 'America/Campo_Grande',
    abbreviation: 'AMT',
    displayName: 'horário de Mato Grosso do Sul',
    utcOffset: '-04:00',
  },
};

/**
 * State groups by timezone
 */
const ACT_STATES = ['AC'];
const AMT_STATES = ['AM'];
const BRT_STATES = [
  'SP', 'RJ', 'MG', 'ES', 'PR', 'SC', 'RS',
  'DF', 'GO', 'TO', 'BA', 'SE', 'AL', 'PE',
  'PB', 'RN', 'CE', 'PI', 'MA', 'PA', 'AP',
];

/**
 * Build the state-to-timezone map
 */
function buildStateTimezoneMap(): Record<string, TimezoneInfo> {
  const map: Record<string, TimezoneInfo> = {};

  for (const state of ACT_STATES) {
    map[state] = TIMEZONE_ACT;
  }

  for (const state of AMT_STATES) {
    map[state] = TIMEZONE_AMT;
  }

  for (const state of BRT_STATES) {
    map[state] = TIMEZONE_BRT;
  }

  return { ...map, ...STATE_SPECIFIC_TIMEZONES };
}

const STATE_TIMEZONE_MAP = buildStateTimezoneMap();

const DEFAULT_TIMEZONE: TimezoneInfo = {
  timezone: 'America/Sao_Paulo',
  abbreviation: 'BRT',
  displayName: 'horário de Brasília',
  utcOffset: '-03:00',
};

/**
 * Obtém informações de timezone baseado na UF
 */
export function getTimezoneByState(stateSlug: string): TimezoneInfo {
  if (!stateSlug) return DEFAULT_TIMEZONE;
  const upperSlug = stateSlug.toUpperCase();
  return STATE_TIMEZONE_MAP[upperSlug] || DEFAULT_TIMEZONE;
}

/**
 * Formata horário com timezone (versão curta com sigla)
 * @param time - Horário no formato HH:MM:SS ou HH:MM
 * @param timezoneAbbr - Sigla do timezone (ex: 'BRT', 'AMT')
 * @returns Horário formatado (ex: "14:00 (BRT)")
 */
export function formatTimeWithTimezone(time: string, timezoneAbbr: string): string {
  if (!time) return '';
  const timeShort = time.substring(0, 5);
  return `${timeShort} (${timezoneAbbr})`;
}

/**
 * Formata horário de audiência com timezone baseado no estado
 * @param hearing - Objeto com hearingTime e state ou timezoneInfo
 * @returns Horário formatado com timezone
 */
export function formatHearingTime(hearing: {
  hearingTime: string;
  state?: { slug?: string };
  timezoneInfo?: TimezoneInfo;
}): string {
  if (!hearing.hearingTime) return '';

  // Se o backend já enviou timezoneInfo, usar diretamente
  if (hearing.timezoneInfo) {
    return formatTimeWithTimezone(hearing.hearingTime, hearing.timezoneInfo.abbreviation);
  }

  // Caso contrário, calcular baseado no estado
  if (hearing.state?.slug) {
    const tzInfo = getTimezoneByState(hearing.state.slug);
    return formatTimeWithTimezone(hearing.hearingTime, tzInfo.abbreviation);
  }

  // Fallback: apenas o horário sem timezone
  return hearing.hearingTime.substring(0, 5);
}

/**
 * Formata data e horário de audiência com timezone
 * @param hearing - Objeto com hearingDate, hearingTime e state ou timezoneInfo
 * @param dateFormatter - Função para formatar a data (ex: dayjs)
 * @returns Data e horário formatados com timezone
 */
export function formatHearingDateTime(
  hearing: {
    hearingDate: string | Date;
    hearingTime: string;
    state?: { slug?: string };
    timezoneInfo?: TimezoneInfo;
  },
  dateFormatter: (date: string | Date) => string,
): string {
  const formattedDate = dateFormatter(hearing.hearingDate);
  const formattedTime = formatHearingTime(hearing);
  return `${formattedDate} ${formattedTime}`;
}

/**
 * Obtém texto descritivo do timezone para exibir em formulários
 * @param stateSlug - Sigla do estado (ex: 'SP', 'AC')
 * @returns Texto descritivo (ex: "horário de Brasília - BRT")
 */
export function getTimezoneDescription(stateSlug: string): string {
  const tzInfo = getTimezoneByState(stateSlug);
  return `${tzInfo.displayName} - ${tzInfo.abbreviation}`;
}
